import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import client from "@/lib/api";
import { toast } from "sonner";
import { ArrowRight } from "@phosphor-icons/react";

const BG = "https://images.unsplash.com/photo-1707077865701-2854996afc4f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDF8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBnbGFzcyUyMG9mZmljZSUyMGJ1aWxkaW5nJTIwYXJjaGl0ZWN0dXJlJTIwYWJzdHJhY3R8ZW58MHx8fHwxNzgzMTk3NzA0fDA&ixlib=rb-4.1.0&q=85";

const DEMO_ROLES = [
    { email: "chairman@performance.demo", label: "Board", tone: "primary" },
    { email: "group.cfo@performance.demo", label: "Group CFO", tone: "default" },
    { email: "corp.finance@performance.demo", label: "Corp. Finance", tone: "default" },
    { email: "cfo.mobility@performance.demo", label: "Division CFO", tone: "default" },
    { email: "auditor@performance.demo", label: "Auditor", tone: "default" },
];

export default function Login() {
    const { login } = useAuth();
    const nav = useNavigate();
    const [email, setEmail] = useState("chairman@performance.demo");
    const [pw, setPw] = useState("Board@2026");
    const [loading, setLoading] = useState(false);
    const [readiness, setReadiness] = useState(null);

    useEffect(() => {
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/public/readiness`)
            .then((r) => r.json()).then(setReadiness).catch(() => {});
        const id = setInterval(() => {
            fetch(`${process.env.REACT_APP_BACKEND_URL}/api/public/readiness`)
                .then((r) => r.json()).then(setReadiness).catch(() => {});
        }, 6000);
        return () => clearInterval(id);
    }, []);

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await login(email, pw);
            toast.success("Welcome back");
            // Fetch role-appropriate home
            try {
                const r = await client.get("/me/home");
                nav(r.data.home || "/");
            } catch {
                nav("/");
            }
        } catch (err) {
            toast.error(err.response?.data?.detail || "Login failed");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2 bg-fp-bg">
            {/* Left panel: hero */}
            <div className="relative hidden lg:flex flex-col justify-between p-12 overflow-hidden" style={{ backgroundImage: `url(${BG})`, backgroundSize: 'cover', backgroundPosition: 'center' }}>
                <div className="absolute inset-0 bg-fp-ink/85 backdrop-blur-[2px]" />
                <div className="relative z-10">
                    <div className="font-serif text-white text-3xl tracking-tight leading-tight">Finance Performance</div>
                    <div className="font-sans text-xs uppercase tracking-[0.3em] text-white/60 mt-1">Prototype</div>
                </div>
                <div className="relative z-10 max-w-lg">
                    <div className="font-sans text-xs uppercase tracking-[0.3em] text-white/60 mb-6">Illustrative · Feb 2026</div>
                    <h1 className="font-serif text-5xl xl:text-6xl leading-[1.05] text-white tracking-tight">
                        Reasoned insight for finance leadership.
                    </h1>
                    <p className="font-sans text-base text-white/70 leading-relaxed mt-6">
                        Illustrative management reporting, performance analysis and transformation dashboard — consolidated view across seven divisions and sixteen jurisdictions, with AI-authored bullet-form narrative, driver-based scenarios and month-end submission workflow.
                    </p>

                    {readiness && (
                        <div className="mt-10 pt-6 border-t border-white/15" data-testid="readiness-scoreboard">
                            <div className="font-sans text-[10px] uppercase tracking-[0.25em] text-white/50 mb-3">Live · Cycle {readiness.cycle} · Consolidation Readiness</div>
                            <div className="flex items-baseline gap-4">
                                <div className="font-mono text-5xl text-white">{readiness.pct_complete}<span className="text-2xl text-white/60">%</span></div>
                                <div className="font-mono text-xs text-white/60">{readiness.complete}/{readiness.total_expected} submissions received</div>
                            </div>
                            <div className="w-full h-1 bg-white/10 mt-3"><div className="h-full bg-white transition-all duration-1000" style={{ width: `${readiness.pct_complete}%` }} /></div>
                            <div className="grid grid-cols-4 gap-3 mt-5 text-white/80">
                                <div><div className="font-mono text-xl text-white">{readiness.approved}</div><div className="font-sans text-[10px] uppercase tracking-widest text-white/50">Approved</div></div>
                                <div><div className="font-mono text-xl text-white">{readiness.submitted + readiness.in_review}</div><div className="font-sans text-[10px] uppercase tracking-widest text-white/50">In Progress</div></div>
                                <div><div className="font-mono text-xl text-white">{readiness.extensions_granted}</div><div className="font-sans text-[10px] uppercase tracking-widest text-white/50">Extensions</div></div>
                                <div><div className="font-mono text-xl text-[#D4A373]">{readiness.overdue}</div><div className="font-sans text-[10px] uppercase tracking-widest text-white/50">Overdue</div></div>
                            </div>
                        </div>
                    )}
                </div>
                <div className="relative z-10 font-mono text-xs text-white/40">
                    Concept prototype by Radhika Mehra · Synthetic illustrative data
                </div>
            </div>

            {/* Right panel: form */}
            <div className="flex items-center justify-center p-8 sm:p-16">
                <form onSubmit={submit} className="w-full max-w-md" data-testid="login-form">
                    <div className="mb-12">
                        <div className="flex items-center justify-between mb-4">
                            <div className="font-sans text-xs uppercase tracking-[0.3em] text-fp-secondary">Sign in</div>
                            <div className="inline-flex items-center gap-2 border border-fp-border px-3 py-1">
                                <span className="w-1.5 h-1.5 rounded-full bg-fp-success animate-pulse" />
                                <span className="font-sans text-[10px] uppercase tracking-[0.25em] text-fp-muted">Confidential · Illustrative</span>
                            </div>
                        </div>
                        <h2 className="font-serif text-4xl tracking-tight text-fp-ink">Access Portal</h2>
                        <p className="font-sans text-sm text-fp-secondary mt-3">Sign in to explore the illustrative management reporting cockpit.</p>
                    </div>

                    <div className="space-y-8">
                        <div>
                            <label className="font-sans text-xs uppercase tracking-[0.2em] text-fp-secondary">Email</label>
                            <input
                                data-testid="login-email"
                                type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                                className="w-full mt-2 border-b border-fp-border bg-transparent pb-2 pt-1 focus:outline-none focus:border-fp-ink font-sans text-fp-ink transition-colors"
                                required
                            />
                        </div>
                        <div>
                            <label className="font-sans text-xs uppercase tracking-[0.2em] text-fp-secondary">Password</label>
                            <input
                                data-testid="login-password"
                                type="password" value={pw} onChange={(e) => setPw(e.target.value)}
                                className="w-full mt-2 border-b border-fp-border bg-transparent pb-2 pt-1 focus:outline-none focus:border-fp-ink font-sans text-fp-ink transition-colors"
                                required
                            />
                        </div>

                        <button
                            data-testid="login-submit"
                            type="submit" disabled={loading}
                            className="group inline-flex items-center gap-3 bg-fp-ink text-white px-8 py-3 font-sans text-sm tracking-wide hover:bg-fp-ink-hover transition-colors disabled:opacity-50"
                        >
                            {loading ? "Signing in…" : "Enter Portal"}
                            <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
                        </button>
                    </div>

                    <div className="mt-16 pt-6 border-t border-fp-border">
                        <div className="font-sans text-xs uppercase tracking-[0.2em] text-fp-muted mb-4">Quick demo access — one click to try a role</div>
                        <div className="flex flex-wrap gap-2">
                            {DEMO_ROLES.map((r) => (
                                <button
                                    key={r.email}
                                    type="button"
                                    data-testid={`demo-role-${r.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                                    onClick={() => { setEmail(r.email); setPw("Board@2026"); }}
                                    className={`font-sans text-xs px-3 py-1.5 border transition-all ${
                                        email === r.email
                                            ? "border-fp-ink bg-fp-ink text-white"
                                            : "border-fp-border text-fp-secondary hover:border-fp-ink hover:text-fp-ink"
                                    }`}
                                >
                                    {r.label}
                                </button>
                            ))}
                        </div>
                        <div className="font-mono text-[10px] text-fp-muted mt-3">Shared password · Board@2026</div>
                    </div>

                    <div className="mt-6 pt-4 border-t border-fp-border">
                        <div className="font-sans text-[10px] text-fp-muted leading-relaxed">
                            Independent work-in-progress prototype using synthetic illustrative data, created to demonstrate finance transformation, performance management and AI-enabled decision-support concepts. Concept prototype by Radhika Mehra.
                        </div>
                    </div>
                </form>
            </div>
        </div>
    );
}
